import React, { useState, useMemo } from 'react';
import { X, Search } from 'lucide-react';
import StarRating from './StarRating';

// Same NULL-to-bottom-regardless-of-direction comparator as Team360Table.js (Stage 4) /
// TeamKpiOverview.js (Stage 6a) — each sortable list in this codebase keeps its own copy
// rather than a shared util (existing convention). Only used here for the picker's fixed
// "best overall first" default order, not interactive re-sorting.
function compareRows(a, b, key, dir) {
  const av = a[key], bv = b[key];
  const aNull = av === null || av === undefined;
  const bNull = bv === null || bv === undefined;
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  if (typeof av === 'string') return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  return dir === 'asc' ? av - bv : bv - av;
}

// The stat shown/sorted by in the candidate list, toggleable (2026-07-24) — Overall plus each
// of the 5 pillars, so a coach filling a slot for physical reasons can see (and rank by)
// physical scores instead of the blended overall.
const STAT_OPTIONS = [
  { key: 'overall', label: 'Overall' },
  { key: 'physical', label: 'Physical' },
  { key: 'technical', label: 'Technical' },
  { key: 'tactical', label: 'Tactical' },
  { key: 'identity', label: 'Identity' },
  { key: 'availability', label: 'Availability' },
];

// Per-position player picker (Stage 7b, confirmed requirement) — clicking a slot opens this
// rather than free-form drag-and-drop. Filters to position-matching candidates by default
// (using the app's existing internal position convention — players.position, base code with
// any Stage 7a slot suffix stripped) but never hard-blocks: "Show all positions" lifts the
// filter for an intentional out-of-position pick. Always excludes players already placed
// elsewhere in the current in-memory lineup (starters or bench), regardless of the filter.
export default function LineupSlotPicker({ open, squadRows, assignedPlayerIds, basePosition, title, onPick, onClose }) {
  const [showAll, setShowAll] = useState(!basePosition);
  const [search, setSearch] = useState('');
  const [statKey, setStatKey] = useState('overall');

  const candidates = useMemo(() => {
    let list = squadRows.filter(r => !assignedPlayerIds.has(r.player_id));
    if (!showAll && basePosition) list = list.filter(r => r.position === basePosition);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(r => (r.name || '').toLowerCase().includes(q));
    }
    return [...list].sort((a, b) => compareRows(a, b, statKey, 'desc'));
  }, [squadRows, assignedPlayerIds, showAll, basePosition, search, statKey]);

  if (!open) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={onClose}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 14, width: 420, maxWidth: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 16px 48px rgba(0,0,0,0.5)' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{title}</div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', padding: 4 }}>
            <X size={16} />
          </button>
        </div>

        <div style={{ padding: '10px 16px', display: 'flex', gap: 8, alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={13} color="var(--text-dimmed)" style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)' }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search player…"
              style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px 7px 28px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13, outline: 'none' }} />
          </div>
          {basePosition && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap', cursor: 'pointer' }}>
              <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} />
              Show all positions
            </label>
          )}
        </div>

        {/* Stat toggle (2026-07-24) — switches which score the list is sorted AND displayed
            by. Overall is the default/existing behavior; picking a pillar shows that pillar's
            number instead (and re-ranks the list by it), not just relabels the same overall. */}
        <div style={{ display: 'flex', gap: 6, padding: '10px 16px', flexWrap: 'wrap', borderBottom: '1px solid var(--border)' }}>
          {STAT_OPTIONS.map(opt => {
            const active = statKey === opt.key;
            return (
              <button key={opt.key} type="button" onClick={() => setStatKey(opt.key)}
                style={{
                  padding: '5px 11px', borderRadius: 999, fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
                  border: `1px solid ${active ? '#2563eb' : 'var(--border-strong)'}`,
                  background: active ? '#2563eb' : 'transparent',
                  color: active ? '#fff' : 'var(--text-secondary)',
                }}>
                {opt.label}
              </button>
            );
          })}
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {!candidates.length && (
            <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--text-dimmed)' }}>
              No eligible players{!showAll && basePosition ? ' at this position' : ''}.
            </div>
          )}
          {candidates.map(r => (
            <button key={r.player_id} onClick={() => onPick(r.player_id)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 16px', border: 'none', borderTop: '1px solid var(--border)', background: 'transparent', cursor: 'pointer', textAlign: 'left' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-row-hover)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <div style={{ width: 30, height: 30, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, background: 'var(--bg-app)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'var(--text-dimmed)' }}>
                {r.photo_url ? <img src={r.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (r.name || '?').charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{r.position || '—'}</div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: r[statKey] == null ? 'var(--text-dimmed)' : 'var(--text-primary)' }}>
                  {r[statKey] == null ? '—' : Math.round(r[statKey])}
                </div>
                {/* Stars are the overall-derived rating — showing them next to a single pillar's
                    raw score would misleadingly imply that pillar has its own star rating. */}
                {statKey === 'overall' && <StarRating value={r.stars} size={10} showNumber={false} gap={1} />}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
